// src/pages/AdminDashboardPage.jsx
import React, { useState, useEffect, useRef } from "react";
import AdminChatsPage from "./AdminChats"; // adjust import path if needed
import PosterChecker from "./Posterchecker";
// ─── CONFIG ──────────────────────────────────────────────────────────────────
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "admin123";
const SESSION_KEY = "admin_authed";

// ─── ICONS ───────────────────────────────────────────────────────────────────
const LockIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ChatIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const UsersIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const EyeIcon = ({ off }) => off ? (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// ─── STYLES ───────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Mono:wght@400;500&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0d0f14;
    --surface: #161921;
    --surface2: #1e2230;
    --border: #2a2f40;
    --accent: #4f7cff;
    --accent2: #a78bfa;
    --text: #e8eaf0;
    --muted: #6b7280;
    --danger: #f87171;
    --success: #34d399;
    --warn: #fbbf24;
    --card-hover: #222840;
  }

  .adm-wrap {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
  }

  /* ── LOCK SCREEN ── */
  .lock-screen {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg);
    position: relative;
    overflow: hidden;
  }

  .lock-bg-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(var(--border) 1px, transparent 1px),
      linear-gradient(90deg, var(--border) 1px, transparent 1px);
    background-size: 40px 40px;
    opacity: 0.35;
    pointer-events: none;
  }

  .lock-glow {
    position: absolute;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(79,124,255,0.12) 0%, transparent 70%);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  .lock-card {
    position: relative;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 52px 48px 44px;
    width: 420px;
    box-shadow: 0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset;
    animation: cardIn 0.5s cubic-bezier(0.16,1,0.3,1) both;
  }

  @keyframes cardIn {
    from { opacity: 0; transform: translateY(24px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .lock-icon {
    width: 60px;
    height: 60px;
    border-radius: 16px;
    background: linear-gradient(135deg, #1a2340, #1e2a5e);
    border: 1px solid rgba(79,124,255,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--accent);
    margin-bottom: 24px;
  }

  .lock-title {
    font-family: 'DM Serif Display', serif;
    font-size: 26px;
    letter-spacing: -0.3px;
    color: var(--text);
    margin-bottom: 6px;
  }

  .lock-sub {
    font-size: 13px;
    color: var(--muted);
    margin-bottom: 32px;
  }

  .lock-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 8px;
  }

  .lock-input-wrap {
    position: relative;
    margin-bottom: 24px;
  }

  .lock-input {
    width: 100%;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 13px 44px 13px 16px;
    font-family: 'DM Mono', monospace;
    font-size: 15px;
    color: var(--text);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    letter-spacing: 2px;
  }

  .lock-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(79,124,255,0.15);
  }

  .lock-input.error {
    border-color: var(--danger);
    animation: shake 0.35s ease;
  }

  @keyframes shake {
    0%,100% { transform: translateX(0); }
    20% { transform: translateX(-8px); }
    40% { transform: translateX(8px); }
    60% { transform: translateX(-5px); }
    80% { transform: translateX(5px); }
  }

  .eye-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--muted);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    transition: color 0.15s;
  }
  .eye-btn:hover { color: var(--text); }

  .lock-btn {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #3b62e8, #5b8aff);
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.15s;
    letter-spacing: 0.02em;
  }

  .lock-btn:hover { opacity: 0.9; transform: translateY(-1px); }
  .lock-btn:active { transform: translateY(0); }

  .lock-error {
    font-size: 12.5px;
    color: var(--danger);
    margin-top: 12px;
    text-align: center;
  }

  /* ── DASHBOARD ── */
  .dashboard {
    min-height: 100vh;
    background: var(--bg);
    animation: fadeIn 0.4s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .dash-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 40px;
    border-bottom: 1px solid var(--border);
    background: var(--surface);
  }

  .dash-logo {
    font-family: 'DM Serif Display', serif;
    font-size: 22px;
    color: var(--text);
    letter-spacing: -0.2px;
  }

  .dash-logo span {
    color: var(--accent);
  }

  .dash-badge {
    background: rgba(79,124,255,0.15);
    color: var(--accent);
    border: 1px solid rgba(79,124,255,0.3);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 3px 10px;
  }

  .logout-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--muted);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    padding: 7px 14px;
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s, background 0.2s;
  }

  .logout-btn:hover {
    color: var(--danger);
    border-color: var(--danger);
    background: rgba(248,113,113,0.06);
  }

  .dash-hero {
    padding: 56px 40px 32px;
    max-width: 960px;
    margin: 0 auto;
  }

  .dash-greeting {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 10px;
  }

  .dash-title {
    font-family: 'DM Serif Display', serif;
    font-size: 40px;
    letter-spacing: -0.5px;
    color: var(--text);
    margin-bottom: 10px;
    line-height: 1.15;
  }

  .dash-desc {
    font-size: 15px;
    color: var(--muted);
    max-width: 480px;
  }

  .dash-divider {
    height: 1px;
    background: var(--border);
    margin: 0 40px 40px;
    max-width: 880px;
    margin-left: auto;
    margin-right: auto;
  }

  .cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 18px;
    padding: 0 40px 60px;
    max-width: 960px;
    margin: 0 auto;
  }

  .dash-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 28px 24px;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, transform 0.2s, box-shadow 0.2s;
    display: flex;
    flex-direction: column;
    gap: 16px;
    animation: cardSlideIn 0.4s cubic-bezier(0.16,1,0.3,1) both;
  }

  .dash-card:nth-child(1) { animation-delay: 0.05s; }
  .dash-card:nth-child(2) { animation-delay: 0.10s; }
  .dash-card:nth-child(3) { animation-delay: 0.15s; }
  .dash-card:nth-child(4) { animation-delay: 0.20s; }

  @keyframes cardSlideIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .dash-card:hover {
    background: var(--card-hover);
    border-color: rgba(79,124,255,0.4);
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.4);
  }

  .card-icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 4px;
  }

  .card-desc {
    font-size: 12.5px;
    color: var(--muted);
    line-height: 1.5;
  }

  .card-arrow {
    font-size: 18px;
    color: var(--muted);
    margin-top: auto;
    transition: transform 0.2s, color 0.2s;
  }

  .dash-card:hover .card-arrow {
    transform: translateX(4px);
    color: var(--accent);
  }

  /* ── BACK BUTTON ── */
  .back-bar {
    padding: 14px 20px;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .back-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--muted);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    padding: 7px 14px;
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s;
  }

  .back-btn:hover {
    color: var(--text);
    border-color: var(--accent);
  }

  .back-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
  }

  .back-muted {
    font-size: 14px;
    color: var(--muted);
  }

  /* ── PLACEHOLDER PAGES ── */
  .placeholder-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 16px;
    color: var(--muted);
  }

  .placeholder-page h2 {
    font-family: 'DM Serif Display', serif;
    font-size: 28px;
    color: var(--text);
  }

  .placeholder-page p {
    font-size: 14px;
  }
`;

// ─── NAV ITEMS ────────────────────────────────────────────────────────────────
const NAV_CARDS = [
  {
    id: "chats",
    title: "Chats",
    desc: "View and reply to user messages across all projects.",
    Icon: ChatIcon,
    color: "#1a3a6b",
    iconColor: "#4f7cff",
  },
  // {
  //   id: "analytics",
  //   title: "Analytics",
  //   desc: "Track usage trends, active users and key metrics.",
  //   Icon: AnalyticsIcon,
  //   color: "#1a3b2a",
  //   iconColor: "#34d399",
  // },
  // {
  //   id: "users",
  //   title: "Users",
  //   desc: "Browse registered users and manage their accounts.",
  //   Icon: UsersIcon,
  //   color: "#3b2a1a",
  //   iconColor: "#fbbf24",
  // },
  // {
  //   id: "settings",
  //   title: "Settings",
  //   desc: "Configure projects, API keys and admin preferences.",
  //   Icon: SettingsIcon,
  //   color: "#2a1a3b",
  //   iconColor: "#a78bfa",
  // },
  {
    id: "posters",
    title: "Posters",
    desc: "Check and validate poster links.",
    Icon: AnalyticsIcon, // you can change icon later
    color: "#1a2f3b",
    iconColor: "#38bdf8",
  },
];

// ─── LOCK SCREEN ─────────────────────────────────────────────────────────────
function LockScreen({ onUnlock }) {
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  function handleSubmit(e) {
    e?.preventDefault();
    if (pwd === ADMIN_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, "1");
      onUnlock();
    } else {
      setError("Incorrect password. Try again.");
      setShake(true);
      setPwd("");
      setTimeout(() => setShake(false), 400);
      inputRef.current?.focus();
    }
  }

  return (
    <div className="lock-screen">
      <div className="lock-bg-grid" />
      <div className="lock-glow" />
      <div className="lock-card">
        <div className="lock-icon"><LockIcon /></div>
        <div className="lock-title">Admin Access</div>
        <div className="lock-sub">Enter your password to continue.</div>

        <div className="lock-label">Password</div>
        <div className="lock-input-wrap">
          <input
            ref={inputRef}
            type={showPwd ? "text" : "password"}
            className={`lock-input${shake ? " error" : ""}`}
            value={pwd}
            onChange={e => { setPwd(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleSubmit()}
            placeholder="••••••••"
            autoComplete="current-password"
          />
          <button
            className="eye-btn"
            onClick={() => setShowPwd(v => !v)}
            tabIndex={-1}
            type="button"
            aria-label={showPwd ? "Hide password" : "Show password"}
          >
            <EyeIcon off={showPwd} />
          </button>
        </div>

        <button className="lock-btn" onClick={handleSubmit}>
          Unlock Dashboard
        </button>

        {error && <div className="lock-error">{error}</div>}
      </div>
    </div>
  );
}

// ─── PLACEHOLDER SUB-PAGES ───────────────────────────────────────────────────
function PlaceholderPage({ title }) {
  return (
    <div className="placeholder-page">
      <h2>{title}</h2>
      <p>This section is coming soon.</p>
    </div>
  );
}

// ─── DASHBOARD HOME ───────────────────────────────────────────────────────────
function DashboardHome({ onNavigate, onLogout }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="dashboard">
      <header className="dash-header">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="dash-logo">Pikchar<span>.</span>admin</div>
          <span className="dash-badge">Secured</span>
        </div>
        <button className="logout-btn" onClick={onLogout}>
          <LogoutIcon /> Sign out
        </button>
      </header>

      <div className="dash-hero">
        <div className="dash-greeting">{greeting}</div>
        <div className="dash-title">Admin Dashboard</div>
        <div className="dash-desc">Manage your projects, users and messages from one place.</div>
      </div>

      <div className="dash-divider" />

      <div className="cards-grid">
        {NAV_CARDS.map(({ id, title, desc, Icon, color, iconColor }) => (
          <div key={id} className="dash-card" onClick={() => onNavigate(id)}>
            <div className="card-icon-wrap" style={{ background: color }}>
              <span style={{ color: iconColor }}><Icon /></span>
            </div>
            <div>
              <div className="card-title">{title}</div>
              <div className="card-desc">{desc}</div>
            </div>
            <div className="card-arrow">→</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PAGE WRAPPER (with back bar) ─────────────────────────────────────────────
function PageWrapper({ pageId, onBack }) {
  const card = NAV_CARDS.find(c => c.id === pageId);

  let Content;
  if (pageId === "chats") {
    Content = <AdminChatsPage />;
  } else if (pageId === "posters") {
    Content = <PosterChecker />;
  } else {
    Content = <PlaceholderPage title={card?.title || pageId} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <div className="back-bar">
        <button className="back-btn" onClick={onBack}>
          ← Back
        </button>
        <span className="back-muted">/</span>
        <span className="back-label">{card?.title || pageId}</span>
      </div>
      {Content}
    </div>
  );
}

// ─── ROOT COMPONENT ───────────────────────────────────────────────────────────
export default function AdminDashboardPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1");
  const [page, setPage] = useState(null); // null = home

  function handleLogout() {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthed(false);
    setPage(null);
  }

  return (
    <div className="adm-wrap">
      <style>{styles}</style>

      {!authed && (
        <LockScreen onUnlock={() => setAuthed(true)} />
      )}

      {authed && page === null && (
        <DashboardHome onNavigate={setPage} onLogout={handleLogout} />
      )}

      {authed && page !== null && (
        <PageWrapper pageId={page} onBack={() => setPage(null)} />
      )}
    </div>
  );
}
